"use client"

import React from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { UIButton } from '../buttons/ui-button'
import { UIInput } from './ui-input'
import { UILabel } from './ui-label'
import { UITypography } from '../display/ui-typography'
import { UIBox } from '../layout/ui-box'
import { UISelect, UISelectTrigger, UISelectContent, UISelectItem, UISelectValue } from './ui-select'
import { UICheckbox } from './ui-checkbox'
import { cn } from '../../lib/utils'
import { Plus, Trash2, Save, AlertCircle, CheckCircle } from 'lucide-react'
// import { useTranslations } from 'next-intl'

// Comprehensive form schema with nested objects, arrays, and conditional validation
const formSchema = z.object({
  // Personal Information (nested object)
  personalInfo: z.object({
    firstName: z.string().min(2, 'First name must be at least 2 characters'),
    lastName: z.string().min(2, 'Last name must be at least 2 characters'),
    email: z.string().email('Please enter a valid email address'),
    phone: z.string().min(10, 'Phone number must be at least 10 digits').optional(),
    dateOfBirth: z.string().optional(),
  }),

  // Address Information (nested object with conditional validation)
  address: z.object({
    street: z.string().min(5, 'Street address must be at least 5 characters'),
    city: z.string().min(2, 'City must be at least 2 characters'),
    country: z.string().min(2, 'Please select a country'),
    postalCode: z.string().min(3, 'Postal code must be at least 3 characters'),
    isSameAsBilling: z.boolean().default(false),
  }),

  // Billing Address (conditional - only required if different from address)
  billingAddress: z.object({
    street: z.string().optional(),
    city: z.string().optional(),
    country: z.string().optional(),
    postalCode: z.string().optional(),
  }).optional(),

  // Skills Array (dynamic array with validation)
  skills: z.array(z.object({
    name: z.string().min(2, 'Skill name must be at least 2 characters'),
    level: z.enum(['beginner', 'intermediate', 'advanced', 'expert']),
    yearsOfExperience: z.number().min(0).max(50),
  })).min(1, 'At least one skill is required'),

  // Employment Information (conditional validation)
  employment: z.object({
    isEmployed: z.boolean(),
    company: z.string().optional(),
    position: z.string().optional(),
    salary: z.number().optional(),
    startDate: z.string().optional(),
  }),

  // Preferences
  preferences: z.object({
    notifications: z.object({
      email: z.boolean().default(true),
      sms: z.boolean().default(false),
      push: z.boolean().default(true),
    }),
    language: z.enum(['ko', 'en']).default('ko'),
    theme: z.enum(['light', 'dark', 'system']).default('system'),
  }),

  // Agreement (conditional validation)
  agreements: z.object({
    terms: z.boolean().refine(val => val === true, 'You must accept the terms and conditions'),
    privacy: z.boolean().refine(val => val === true, 'You must accept the privacy policy'),
    marketing: z.boolean().default(false),
  }),
}).refine((data) => {
  // Conditional validation: if employed, company and position are required
  if (data.employment.isEmployed) {
    return data.employment.company && data.employment.position
  }
  return true
}, {
  message: "Company and position are required when employed",
  path: ["employment"],
}).refine((data) => {
  // Conditional validation: if billing address is different, all fields are required
  if (!data.address.isSameAsBilling && data.billingAddress) {
    return data.billingAddress.street && data.billingAddress.city &&
           data.billingAddress.country && data.billingAddress.postalCode
  }
  return true
}, {
  message: "All billing address fields are required when different from main address",
  path: ["billingAddress"],
})

export type UIFormSchema = z.infer<typeof formSchema>

export interface UIFormProps {
  defaultValues?: Partial<UIFormSchema>
  onSubmit?: (data: UIFormSchema) => Promise<void> | void
  onAutoSave?: (data: Partial<UIFormSchema>) => Promise<void> | void
  variant?: 'default' | 'card' | 'modal'
  size?: 'sm' | 'default' | 'lg'
  autoSave?: boolean
  autoSaveDelay?: number
  disabled?: boolean
  className?: string
}

/**
 * UIForm - Comprehensive Form System with react-hook-form + zod
 *
 * Features:
 * - Nested object validation (personalInfo, address, employment)
 * - Dynamic array fields (skills)
 * - Conditional validation (employment, billing address)
 * - Real-time validation with error display
 * - Auto-save functionality
 * - Full TypeScript support
 * - Internationalization ready
 */
export const UIForm = React.forwardRef<HTMLFormElement, UIFormProps>(
  ({
    defaultValues,
    onSubmit,
    onAutoSave,
    variant = 'default',
    size = 'default',
    autoSave = false,
    autoSaveDelay = 2000,
    disabled = false,
    className,
    ...props
  }, ref) => {
    // const t = useTranslations('components.composed.forms.advanced')
    const [isSubmitting, setIsSubmitting] = React.useState(false)
    const [isSaving, setIsSaving] = React.useState(false)
    const [lastSaved, setLastSaved] = React.useState<Date | null>(null)

    const form = useForm<UIFormSchema>({
      resolver: zodResolver(formSchema) as any,
      defaultValues: {
        personalInfo: {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          dateOfBirth: '',
        },
        address: {
          street: '',
          city: '',
          country: '',
          postalCode: '',
          isSameAsBilling: true,
        },
        billingAddress: {
          street: '',
          city: '',
          country: '',
          postalCode: '',
        },
        skills: [
          {
            name: '',
            level: 'beginner',
            yearsOfExperience: 0,
          }
        ],
        employment: {
          isEmployed: false,
          company: '',
          position: '',
          salary: 0,
          startDate: '',
        },
        preferences: {
          notifications: {
            email: true,
            sms: false,
            push: true,
          },
          language: 'ko',
          theme: 'system',
        },
        agreements: {
          terms: false,
          privacy: false,
          marketing: false,
        },
        ...defaultValues,
      },
      mode: 'onChange',
    })

    const { fields: skillsFields, append: appendSkill, remove: removeSkill } = useFieldArray({
      control: form.control,
      name: 'skills',
    })

    const watchedData = form.watch()
    const isEmployed = form.watch('employment.isEmployed')
    const isSameAsBilling = form.watch('address.isSameAsBilling')

    // Auto-save functionality
    React.useEffect(() => {
      if (!autoSave || !onAutoSave) return

      const timeoutId = setTimeout(async () => {
        const currentData = form.getValues()
        if (Object.keys(form.formState.dirtyFields).length > 0) {
          setIsSaving(true)
          try {
            await onAutoSave(currentData)
            setLastSaved(new Date())
          } catch (error) {
            console.error('Auto-save failed:', error)
          } finally {
            setIsSaving(false)
          }
        }
      }, autoSaveDelay)

      return () => clearTimeout(timeoutId)
    }, [watchedData, autoSave, onAutoSave, autoSaveDelay, form])

    const handleSubmit = async (data: UIFormSchema) => {
      if (!onSubmit) return

      setIsSubmitting(true)
      try {
        await onSubmit(data)
        form.reset(data) // Reset dirty state after successful submit
      } catch (error) {
        console.error('Form submission failed:', error)
      } finally {
        setIsSubmitting(false)
      }
    }

    const addSkill = () => {
      appendSkill({
        name: '',
        level: 'beginner',
        yearsOfExperience: 0,
      })
    }

    const getFieldError = (path: string): string | undefined => {
      const pathArray = path.split('.')
      let error: any = form.formState.errors

      for (const key of pathArray) {
        if (error && typeof error === 'object' && key in error) {
          error = error[key]
        } else {
          return undefined
        }
      }

      return error?.message
    }

    const containerClasses = cn(
      "space-y-8",
      {
        'p-6 border border-border rounded-lg bg-card': variant === 'card',
        'p-4 space-y-6': variant === 'modal',
        'max-w-2xl': size === 'sm',
        'max-w-4xl': size === 'default',
        'max-w-6xl': size === 'lg',
      },
      className
    )

    return (
      <form
        ref={ref}
        onSubmit={form.handleSubmit(handleSubmit as any)}
        className={containerClasses}
        {...props}
      >
        {/* Auto-save status */}
        {autoSave && (
          <UIBox className="flex items-center justify-between text-sm text-muted-foreground">
            <UIBox className="flex items-center gap-2">
              {isSaving ? (
                <>
                  <UIBox className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  <UITypography as="span" variant="small" size="sm">Saving...</UITypography>
                </>
              ) : lastSaved ? (
                <>
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <UITypography as="span" variant="small" size="sm">Last saved: {lastSaved.toLocaleTimeString()}</UITypography>
                </>
              ) : null}
            </UIBox>
          </UIBox>
        )}

        {/* Personal Information Section */}
        <UIBox className="space-y-4">
          <UITypography variant="h3" size="lg" weight="semibold">Personal Information</UITypography>
          <UIBox className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <UIBox className="space-y-2">
              <UILabel htmlFor="firstName">First Name *</UILabel>
              <UIInput
                id="firstName"
                {...form.register('personalInfo.firstName')}
                disabled={disabled}
                className={getFieldError('personalInfo.firstName') ? 'border-destructive' : ''}
              />
              {getFieldError('personalInfo.firstName') && (
                <UITypography as="p" variant="small" size="sm" color="destructive" className="flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {getFieldError('personalInfo.firstName')}
                </UITypography>
              )}
            </UIBox>

            <UIBox className="space-y-2">
              <UILabel htmlFor="lastName">Last Name *</UILabel>
              <UIInput
                id="lastName"
                {...form.register('personalInfo.lastName')}
                disabled={disabled}
                className={getFieldError('personalInfo.lastName') ? 'border-destructive' : ''}
              />
              {getFieldError('personalInfo.lastName') && (
                <UITypography as="p" variant="small" size="sm" color="destructive" className="flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {getFieldError('personalInfo.lastName')}
                </UITypography>
              )}
            </UIBox>

            <UIBox className="space-y-2">
              <UILabel htmlFor="email">Email Address *</UILabel>
              <UIInput
                id="email"
                type="email"
                {...form.register('personalInfo.email')}
                disabled={disabled}
                className={getFieldError('personalInfo.email') ? 'border-destructive' : ''}
              />
              {getFieldError('personalInfo.email') && (
                <UITypography as="p" variant="small" size="sm" color="destructive" className="flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {getFieldError('personalInfo.email')}
                </UITypography>
              )}
            </UIBox>

            <UIBox className="space-y-2">
              <UILabel htmlFor="phone">Phone Number</UILabel>
              <UIInput
                id="phone"
                type="tel"
                {...form.register('personalInfo.phone')}
                disabled={disabled}
                className={getFieldError('personalInfo.phone') ? 'border-destructive' : ''}
              />
              {getFieldError('personalInfo.phone') && (
                <UITypography as="p" variant="small" size="sm" color="destructive" className="flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {getFieldError('personalInfo.phone')}
                </UITypography>
              )}
            </UIBox>
          </UIBox>
        </UIBox>

        {/* Address Section */}
        <UIBox className="space-y-4">
          <UITypography variant="h3" size="lg" weight="semibold">Address Information</UITypography>
          <UIBox className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <UIBox className="space-y-2 md:col-span-2">
              <UILabel htmlFor="street">Street Address *</UILabel>
              <UIInput
                id="street"
                {...form.register('address.street')}
                disabled={disabled}
                className={getFieldError('address.street') ? 'border-destructive' : ''}
              />
              {getFieldError('address.street') && (
                <UITypography as="p" variant="small" size="sm" color="destructive" className="flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {getFieldError('address.street')}
                </UITypography>
              )}
            </UIBox>

            <UIBox className="space-y-2">
              <UILabel htmlFor="city">City *</UILabel>
              <UIInput
                id="city"
                {...form.register('address.city')}
                disabled={disabled}
                className={getFieldError('address.city') ? 'border-destructive' : ''}
              />
              {getFieldError('address.city') && (
                <UITypography as="p" variant="small" size="sm" color="destructive" className="flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {getFieldError('address.city')}
                </UITypography>
              )}
            </UIBox>

            <UIBox className="space-y-2">
              <UILabel htmlFor="country">Country *</UILabel>
              <UISelect
                onValueChange={(value) => form.setValue('address.country', value, { shouldValidate: true })}
                defaultValue={form.getValues('address.country')}
                disabled={disabled}
              >
                <UISelectTrigger className={getFieldError('address.country') ? 'border-destructive' : ''}>
                  <UISelectValue placeholder="Select country" />
                </UISelectTrigger>
                <UISelectContent>
                  <UISelectItem value="KR">South Korea</UISelectItem>
                  <UISelectItem value="US">United States</UISelectItem>
                  <UISelectItem value="JP">Japan</UISelectItem>
                  <UISelectItem value="CN">China</UISelectItem>
                  <UISelectItem value="GB">United Kingdom</UISelectItem>
                </UISelectContent>
              </UISelect>
              {getFieldError('address.country') && (
                <UITypography as="p" variant="small" size="sm" color="destructive" className="flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {getFieldError('address.country')}
                </UITypography>
              )}
            </UIBox>

            <UIBox className="space-y-2">
              <UILabel htmlFor="postalCode">Postal Code *</UILabel>
              <UIInput
                id="postalCode"
                {...form.register('address.postalCode')}
                disabled={disabled}
                className={getFieldError('address.postalCode') ? 'border-destructive' : ''}
              />
              {getFieldError('address.postalCode') && (
                <UITypography as="p" variant="small" size="sm" color="destructive" className="flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {getFieldError('address.postalCode')}
                </UITypography>
              )}
            </UIBox>

            <UIBox className="md:col-span-2">
              <UIBox className="flex items-center space-x-2">
                <UICheckbox
                  id="isSameAsBilling"
                  checked={isSameAsBilling}
                  onCheckedChange={(checked) => form.setValue('address.isSameAsBilling', !!checked)}
                  disabled={disabled}
                />
                <UILabel htmlFor="isSameAsBilling">Billing address is same as above</UILabel>
              </UIBox>
            </UIBox>
          </UIBox>
        </UIBox>

        {/* Billing Address Section (conditional) */}
        {!isSameAsBilling && (
          <UIBox className="space-y-4">
            <UITypography variant="h3" size="lg" weight="semibold">Billing Address</UITypography>
            <UIBox className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <UIBox className="space-y-2 md:col-span-2">
                <UILabel htmlFor="billingStreet">Street Address *</UILabel>
                <UIInput
                  id="billingStreet"
                  {...form.register('billingAddress.street')}
                  disabled={disabled}
                  className={getFieldError('billingAddress.street') ? 'border-destructive' : ''}
                />
                {getFieldError('billingAddress.street') && (
                  <UITypography as="p" variant="small" size="sm" color="destructive" className="flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {getFieldError('billingAddress.street')}
                  </UITypography>
                )}
              </UIBox>

              <UIBox className="space-y-2">
                <UILabel htmlFor="billingCity">City *</UILabel>
                <UIInput
                  id="billingCity"
                  {...form.register('billingAddress.city')}
                  disabled={disabled}
                  className={getFieldError('billingAddress.city') ? 'border-destructive' : ''}
                />
                {getFieldError('billingAddress.city') && (
                  <UITypography as="p" variant="small" size="sm" color="destructive" className="flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {getFieldError('billingAddress.city')}
                  </UITypography>
                )}
              </UIBox>

              <UIBox className="space-y-2">
                <UILabel htmlFor="billingPostalCode">Postal Code *</UILabel>
                <UIInput
                  id="billingPostalCode"
                  {...form.register('billingAddress.postalCode')}
                  disabled={disabled}
                  className={getFieldError('billingAddress.postalCode') ? 'border-destructive' : ''}
                />
                {getFieldError('billingAddress.postalCode') && (
                  <UITypography as="p" variant="small" size="sm" color="destructive" className="flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {getFieldError('billingAddress.postalCode')}
                  </UITypography>
                )}
              </UIBox>
            </UIBox>
          </UIBox>
        )}

        {/* Skills Section (Dynamic Array) */}
        <UIBox className="space-y-4">
          <UIBox className="flex items-center justify-between">
            <UITypography variant="h3" size="lg" weight="semibold">Skills</UITypography>
            <UIButton
              type="button"
              variant="outline"
              size="sm"
              onClick={addSkill}
              disabled={disabled}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Skill
            </UIButton>
          </UIBox>

          <UIBox className="space-y-4">
            {skillsFields.map((field, index) => (
              <UIBox key={field.id} className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 border border-border rounded-lg">
                <UIBox className="space-y-2">
                  <UILabel htmlFor={`skill-name-${index}`}>Skill Name *</UILabel>
                  <UIInput
                    id={`skill-name-${index}`}
                    {...form.register(`skills.${index}.name`)}
                    disabled={disabled}
                    className={getFieldError(`skills.${index}.name`) ? 'border-destructive' : ''}
                  />
                  {getFieldError(`skills.${index}.name`) && (
                    <UITypography as="p" variant="small" size="sm" color="destructive" className="flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {getFieldError(`skills.${index}.name`)}
                    </UITypography>
                  )}
                </UIBox>

                <UIBox className="space-y-2">
                  <UILabel htmlFor={`skill-level-${index}`}>Level</UILabel>
                  <UISelect
                    onValueChange={(value) => form.setValue(`skills.${index}.level`, value as any, { shouldValidate: true })}
                    defaultValue={form.getValues(`skills.${index}.level`)}
                    disabled={disabled}
                  >
                    <UISelectTrigger>
                      <UISelectValue />
                    </UISelectTrigger>
                    <UISelectContent>
                      <UISelectItem value="beginner">Beginner</UISelectItem>
                      <UISelectItem value="intermediate">Intermediate</UISelectItem>
                      <UISelectItem value="advanced">Advanced</UISelectItem>
                      <UISelectItem value="expert">Expert</UISelectItem>
                    </UISelectContent>
                  </UISelect>
                </UIBox>

                <UIBox className="space-y-2">
                  <UILabel htmlFor={`skill-years-${index}`}>Years of Experience</UILabel>
                  <UIInput
                    id={`skill-years-${index}`}
                    type="number"
                    min="0"
                    max="50"
                    {...form.register(`skills.${index}.yearsOfExperience`, { valueAsNumber: true })}
                    disabled={disabled}
                    className={getFieldError(`skills.${index}.yearsOfExperience`) ? 'border-destructive' : ''}
                  />
                  {getFieldError(`skills.${index}.yearsOfExperience`) && (
                    <UITypography as="p" variant="small" size="sm" color="destructive" className="flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {getFieldError(`skills.${index}.yearsOfExperience`)}
                    </UITypography>
                  )}
                </UIBox>

                <UIBox className="flex items-end">
                  <UIButton
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeSkill(index)}
                    disabled={disabled || skillsFields.length === 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </UIButton>
                </UIBox>
              </UIBox>
            ))}
          </UIBox>

          {getFieldError('skills') && (
            <UITypography variant="p" size="sm" className="text-destructive flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {getFieldError('skills')}
            </UITypography>
          )}
        </UIBox>

        {/* Employment Section (Conditional) */}
        <UIBox className="space-y-4">
          <UITypography variant="h3" size="lg" weight="semibold">Employment Information</UITypography>

          <UIBox className="flex items-center space-x-2">
            <UICheckbox
              id="isEmployed"
              checked={isEmployed}
              onCheckedChange={(checked) => form.setValue('employment.isEmployed', !!checked)}
              disabled={disabled}
            />
            <UILabel htmlFor="isEmployed">I am currently employed</UILabel>
          </UIBox>

          {isEmployed && (
            <UIBox className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <UIBox className="space-y-2">
                <UILabel htmlFor="company">Company *</UILabel>
                <UIInput
                  id="company"
                  {...form.register('employment.company')}
                  disabled={disabled}
                  className={getFieldError('employment.company') ? 'border-destructive' : ''}
                />
                {getFieldError('employment.company') && (
                  <UITypography as="p" variant="small" size="sm" color="destructive" className="flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {getFieldError('employment.company')}
                  </UITypography>
                )}
              </UIBox>

              <UIBox className="space-y-2">
                <UILabel htmlFor="position">Position *</UILabel>
                <UIInput
                  id="position"
                  {...form.register('employment.position')}
                  disabled={disabled}
                  className={getFieldError('employment.position') ? 'border-destructive' : ''}
                />
                {getFieldError('employment.position') && (
                  <UITypography as="p" variant="small" size="sm" color="destructive" className="flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {getFieldError('employment.position')}
                  </UITypography>
                )}
              </UIBox>

              <UIBox className="space-y-2">
                <UILabel htmlFor="salary">Annual Salary</UILabel>
                <UIInput
                  id="salary"
                  type="number"
                  min="0"
                  {...form.register('employment.salary', { valueAsNumber: true })}
                  disabled={disabled}
                />
              </UIBox>

              <UIBox className="space-y-2">
                <UILabel htmlFor="startDate">Start Date</UILabel>
                <UIInput
                  id="startDate"
                  type="date"
                  {...form.register('employment.startDate')}
                  disabled={disabled}
                />
              </UIBox>
            </UIBox>
          )}

          {getFieldError('employment') && (
            <UITypography variant="p" size="sm" className="text-destructive flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {getFieldError('employment')}
            </UITypography>
          )}
        </UIBox>

        {/* Agreements Section */}
        <UIBox className="space-y-4">
          <UITypography variant="h3" size="lg" weight="semibold">Agreements</UITypography>

          <UIBox className="space-y-3">
            <UIBox className="flex items-center space-x-2">
              <UICheckbox
                id="terms"
                {...form.register('agreements.terms')}
                disabled={disabled}
              />
              <UILabel htmlFor="terms">I accept the terms and conditions *</UILabel>
            </UIBox>
            {getFieldError('agreements.terms') && (
              <UITypography variant="p" size="sm" className="text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {getFieldError('agreements.terms')}
              </UITypography>
            )}

            <UIBox className="flex items-center space-x-2">
              <UICheckbox
                id="privacy"
                {...form.register('agreements.privacy')}
                disabled={disabled}
              />
              <UILabel htmlFor="privacy">I accept the privacy policy *</UILabel>
            </UIBox>
            {getFieldError('agreements.privacy') && (
              <UITypography variant="p" size="sm" className="text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {getFieldError('agreements.privacy')}
              </UITypography>
            )}

            <UIBox className="flex items-center space-x-2">
              <UICheckbox
                id="marketing"
                {...form.register('agreements.marketing')}
                disabled={disabled}
              />
              <UILabel htmlFor="marketing">I agree to receive marketing communications</UILabel>
            </UIBox>
          </UIBox>
        </UIBox>

        {/* Form Actions */}
        <UIBox className="flex items-center justify-between pt-6 border-t border-border">
          <UIBox className="text-sm text-muted-foreground">
            * Required fields
          </UIBox>

          <UIBox className="flex items-center gap-3">
            {autoSave && onAutoSave && (
              <UIButton
                type="button"
                variant="outline"
                onClick={async () => {
                  setIsSaving(true)
                  try {
                    await onAutoSave(form.getValues())
                    setLastSaved(new Date())
                  } catch (error) {
                    console.error('Manual save failed:', error)
                  } finally {
                    setIsSaving(false)
                  }
                }}
                disabled={disabled || isSaving}
              >
                <Save className="h-4 w-4 mr-2" />
                {isSaving ? 'Saving...' : 'Save Draft'}
              </UIButton>
            )}

            <UIButton
              type="submit"
              disabled={disabled || isSubmitting || !form.formState.isValid}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Form'}
            </UIButton>
          </UIBox>
        </UIBox>
      </form>
    )
  }
)

UIForm.displayName = 'UIForm'

export default UIForm